import React from 'react';
import { Link } from 'react-router-dom';
import { Music, Facebook, Instagram, Youtube, Mail, Phone, MapPin } from 'lucide-react';

// Custom Linktree Icon Component with proper sizing
const LinktreeIcon: React.FC<{ className?: string }> = ({ className = "h-10 w-10" }) => (
  <svg 
    viewBox="0 0 24 24" 
    className={className}
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M13.73 21.01c-.59 0-1.07-.48-1.07-1.07v-4.59h-1.32v4.59c0 .59-.48 1.07-1.07 1.07s-1.07-.48-1.07-1.07v-4.59H7.88c-.59 0-1.07-.48-1.07-1.07s.48-1.07 1.07-1.07h1.32v-1.32H7.88c-.59 0-1.07-.48-1.07-1.07s.48-1.07 1.07-1.07h1.32V9.22c0-.59.48-1.07 1.07-1.07s1.07.48 1.07 1.07v1.32h1.32V9.22c0-.59.48-1.07 1.07-1.07s1.07.48 1.07 1.07v1.32h1.32c.59 0 1.07.48 1.07 1.07s-.48 1.07-1.07 1.07h-1.32v1.32h1.32c.59 0 1.07.48 1.07 1.07s-.48 1.07-1.07 1.07h-1.32v4.59c0 .59-.48 1.07-1.07 1.07z"/>
  </svg>
);

const Footer: React.FC = () => {
  return (
    <footer className="bg-black text-gray-200">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Company Info & Logo */}
          <div>
            <Link to="/" className="flex items-center space-x-2">
              <Music className="h-8 w-8 text-red-700" />
              <span className="text-xl font-bold text-white">Strum Music</span>
            </Link>
            <p className="mt-4 text-gray-300 text-sm">
              Empowering musicians with quality instruments since 1995. Strum Music is your trusted partner for all things musical.
            </p>
            <div className="mt-6 flex space-x-4">
              <a 
                href="https://www.facebook.com/people/Strum-music-shop/61562796578326/?mibextid=LQQJ4d" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-blue-500 transition-colors"
                aria-label="Follow us on Facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a 
                href="https://www.instagram.com/strum.music.shop/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-pink-500 transition-colors"
                aria-label="Follow us on Instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a 
                href="https://linktr.ee/strummusic" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-green-500 transition-colors"
                aria-label="Visit our Linktree"
              >
                <LinktreeIcon className="h-5 w-5" />
              </a>
              <a 
                href="https://maps.app.goo.gl/EKfjTfWXRCBs7Qds9" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-red-500 transition-colors"
                aria-label="Find us on Google Maps"
              >
                <MapPin className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/products" className="text-gray-300 hover:text-red-700 transition-colors">All Products</Link>
              </li>
              <li>
                <Link to="/products/guitars" className="text-gray-300 hover:text-red-700 transition-colors">Guitars</Link>
              </li>
              <li>
                <Link to="/products/drums" className="text-gray-300 hover:text-red-700 transition-colors">Drums & Percussion</Link>
              </li>
              <li>
                <Link to="/products/keyboards" className="text-gray-300 hover:text-red-700 transition-colors">Keyboards & Pianos</Link>
              </li>
              <li>
                <Link to="/products/studio" className="text-gray-300 hover:text-red-700 transition-colors">Studio Equipment</Link>
              </li>
              <li>
                <Link to="/rental" className="text-gray-300 hover:text-red-700 transition-colors">Rental</Link>
              </li>
              <li>
                <Link to="/open-box" className="text-gray-300 hover:text-red-700 transition-colors">Open Box</Link>
              </li>
              <li>
                <Link to="/factory-returned" className="text-gray-300 hover:text-red-700 transition-colors">Factory Returned</Link>
              </li>
            </ul>
          </div>

          {/* Information */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">Information</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/about" className="text-gray-300 hover:text-red-700 transition-colors">About Us</Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-300 hover:text-red-700 transition-colors">Contact Us</Link>
              </li>
            </ul>
          </div>

          {/* Policies & Legal */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">Policies & Legal</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/privacy-policy" className="text-gray-300 hover:text-red-700 transition-colors">Privacy Policy</Link>
              </li>
              <li>
                <Link to="/terms-conditions" className="text-gray-300 hover:text-red-700 transition-colors">Terms & Conditions</Link>
              </li>
              <li>
                <Link to="/shipping-delivery-policy" className="text-gray-300 hover:text-red-700 transition-colors">Shipping & Delivery Policy</Link>
              </li>
              <li>
                <Link to="/cancellation-refund-policy" className="text-gray-300 hover:text-red-700 transition-colors">Cancellation & Refund Policy</Link>
              </li>
            </ul>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-2">
                <MapPin className="h-5 w-5 text-red-700 mt-0.5" />
                <div>
                  <a 
                    href="https://maps.app.goo.gl/EKfjTfWXRCBs7Qds9" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:text-red-700 transition-colors"
                  >
                    C-7/13, Lawrence Road<br />Keshav Puram, Delhi 110035
                  </a>
                </div>
              </li>
              <li className="flex items-center space-x-2">
                <Phone className="h-5 w-5 text-red-700" />
                <a href="tel:+918076883098" className="hover:text-red-700 transition-colors">+91-8076883098</a>
              </li>
              <li className="flex items-center space-x-2">
                <Mail className="h-5 w-5 text-red-700" />
                <a href="mailto:strum.music.co@gmail.com" className="hover:text-red-700 transition-colors">strum.music.co@gmail.com</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-gray-700 text-center text-gray-400 text-sm">
          <p>© {new Date().getFullYear()} Strum Music. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;